<template>
  <div>
    <section>
      <div class="">
        <div class="page-table-container mb-6 bg-white rounded shadow">
          <!-- Table -->
          <div class="table-responsive">
            <table class="table mb-0 table-borderless table-striped small">
              <thead>
                <tr class="text-secondary">
                  <sortable-table-header field="id" label="id" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getProjects)" />
                  <sortable-table-header field="name" label="name" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getProjects)" />
                  <sortable-table-header field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getProjects)" />
                  <sortable-table-header field="priority" label="priority" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getProjects)" />
                  <th v-if="helper.checkAccess('view_tasks') > 0" class="pt-4 pb-3 px-6">{{$t('tasks')}}</th>
                  <sortable-table-header field="start_date" label="start_date" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getProjects)" />
                  <sortable-table-header field="end_date" label="end_date" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getProjects)" />
                  <th class="pt-4 pb-3 px-6">{{$t('team_size')}}</th>
                  <th class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="project in projects" :key="project.id">
                  <td class="py-5 px-6">
                    <router-link :to="`/projects/${project.id}`">
                      <b>{{ project.id.substring(0, 8) }}</b>
                    </router-link>
                  </td>
                  <td class="py-5 px-6">{{ project.name }}</td>
                  <td class="py-5 px-6">
                    <span :class="'badge bg-' + getStatusColor(project.status)">
                      {{ $t(project.status) }}
                    </span>
                  </td>
                  <td class="py-5 px-6">
                    <span :class="'badge bg-' + getPriorityColor(project.priority)">
                      {{ $t(project.priority) }}
                    </span>
                  </td>
                  <td v-if="helper.checkAccess('view_tasks') > 0" class="py-5 px-6">
                    {{ getCompletedTaskCount(project) }}/{{ getTaskCount(project) }}
                  </td>
                  <td class="py-5 px-6">{{ formatDate(project.start_date) }}</td>
                  <td class="py-5 px-6">{{ formatDate(project.end_date) }}</td>
                  <td class="py-5 px-6">{{ project.assignments ? project.assignments.length : 0 }}</td>
                  <td class="py-5 px-6">
                    <button v-if="helper.checkAccess('view_projects') > 0" @click="goDetails(project.id)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                      <AppIcon name="bi-eye" class="bi bi-eye" />
                      {{$t('details')}}
                    </button>
                    <button v-if="helper.checkAccess('edit_projects') > 0" @click="editRow(project)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                      <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
                      {{$t('edit')}}
                    </button>
                    <button v-if="helper.checkAccess('delete_projects') > 0" @click="deleteRow(project)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                      <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
                      {{$t('delete')}}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Pagination -->
            <div v-if="total > projects.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
              <compact-pagination :pages="pagination" :current-page="page" @change="getProjects" />
            </div>
          </div>

          <table-loading-bar v-if="loading" class="page-table-state text-center" />
          <div v-if="loading === false && !projects.length" class="page-table-state text-center py-5 my-5 bg-white rounded shadow-sm border" style="border-style: dashed !important;">
            <AppIcon name="bi-kanban" class="bi bi-kanban text-muted" style="font-size: 3rem;" />
            <h4 class="mt-3">{{ $t('no_projects_added') }}</h4>
            <p class="text-muted">{{ $t('start_by_adding_projects') }}</p>
            <template v-if="helper.checkAccess('create_projects') > 0">
              <button class="btn btn-outline-primary mt-2" @click="$emit('createModalStatus'); $parent.openCreateModal = true">
                {{ $t('add_first_project') }}
              </button>
            </template>
          </div>
        </div>

        <!-- Per page selector -->
        <div class="col-sm-7">
          {{$t('show')}}
          <select v-model="per_page" class="form-select mb-3" style="cursor:pointer;margin-right: 5px;width:100px;display:inline-block">
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          {{$t('entities')}}
        </div>
      </div>
    </section>

    <!-- Create/Edit Modal -->
    <add-edit-project-modal 
      v-if="createModalisOpen || editModalisOpen" 
      @modalClosed="closeModal"
      :isModalOpen="createModalisOpen || editModalisOpen" 
      :data="selectedProject" 
      :action="createModalisOpen ? 'create' : 'update'"
      @refresh="refresh"
    ></add-edit-project-modal>
  </div>
</template>

<script>
import helper from '../helper'
import AddEditProjectModal from './project/AddEditProjectModal.vue'
import sortableList from '../mixins/sortableList'

export default {
  name: 'ProjectComponent',
  persistedFilterState: { key: 'projects-table', fields: ['sortField', 'sortDirection'] },
  mixins: [sortableList],
  components: {
    AddEditProjectModal
  },
  props: {
    keyword: {
      type: String,
      default: ''
    },
    openCreateModal: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({
        status: '',
        priority: ''
      })
    }
  },
  data() {
    return {
      helper,
      projects: [],
      total: 0,
      page: 1,
      per_page: 10,
      loading: false,
      createModalisOpen: false,
      editModalisOpen: false,
      selectedProject: {}
    }
  },
  computed: {
    pagination() {
      const pages = Math.ceil(this.total / this.per_page)
      return Array.from({ length: pages }, (_, i) => i + 1)
    }
  },
  watch: {
    keyword() {
      this.getProjects()
    },
    per_page() {
      this.getProjects()
    },
    openCreateModal(val) {
      if (val) {
        this.selectedProject = {}
        this.createModalisOpen = true
      }
    },
    filters: {
      handler() {
        this.getProjects()
      },
      deep: true
    }
  },
  mounted() {
    this.getProjects()
  },
  methods: {
    getTaskCount(project) {
      return Array.isArray(project.tasks) ? project.tasks.length : 0
    },
    getCompletedTaskCount(project) {
      return Array.isArray(project.tasks)
        ? project.tasks.filter(task => task.status === 'completed').length
        : 0
    },
    async getProjects(page = 1) {
      this.loading = true
      this.page = page
      try {
        const params = new URLSearchParams({ page: this.page, per_page: this.per_page })
        this.appendSortParams(params)
        
        if (this.keyword) {
          params.append('search', this.keyword)
        }
        if (this.filters.status) {
          params.append('status', this.filters.status)
        }
        if (this.filters.priority) {
          params.append('priority', this.filters.priority)
        }

        const url = `${process.env.VUE_APP_API}/projects?${params.toString()}`
        const response = await helper.customGet(url)
        this.projects = response.data.projects
        this.total = response.data.total
      } catch (error) {
        alert('Error loading projects:', error)
      } finally {
        this.loading = false
      }
    },
    goDetails(id) {
      if (helper.checkAccess('view_projects') <= 0) return
      this.$router.push(`/projects/${id}`)
    },
    editRow(project) {
      if (helper.checkAccess('edit_projects') <= 0) return
      this.selectedProject = { ...project }
      this.editModalisOpen = true
    },
    async deleteRow(project) {
      if (helper.checkAccess('delete_projects') <= 0) return
      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      })
      if (result.isConfirmed) {
        try {
          await helper.customDelete(`${process.env.VUE_APP_API}/projects/${project.id}`, { showToast: true })
          this.projects = helper.withoutDeletedRecords(this.projects, project.id)
          this.total = Math.max(0, this.total - 1)
        } catch (error) {
          alert('Error deleting project:', error)
        }
      }
    },
    closeModal() {
      this.createModalisOpen = false
      this.editModalisOpen = false
      this.$emit('createModalStatus')
    },
    refresh() {
      this.getProjects()
      this.closeModal()
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString()
    },
    getStatusColor(status) {
      const colors = {
        draft: 'secondary',
        active: 'primary',
        on_hold: 'warning',
        completed: 'success',
        cancelled: 'danger'
      }
      return colors[status] || 'secondary'
    },
    getPriorityColor(priority) {
      const colors = {
        low: 'info',
        medium: 'primary',
        high: 'warning',
        urgent: 'danger'
      }
      return colors[priority] || 'primary'
    }
  }
}
</script>

<style scoped>
    .table-borderless>:not(caption)>*>*{
        padding: 5px !important
    }
    .table-borderless>:not(caption)>*>*{
        padding: 5px !important
    }
</style>
